// Local Notification Storage Service
// Stores FCM notifications in localStorage for displaying in the bell icon

const STORAGE_KEY = 'fcm_notifications';
const MAX_NOTIFICATIONS = 50; // Keep only the last 50 notifications

class NotificationStorageService {
  /**
   * Get all stored notifications
   */
  getNotifications() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading notifications from localStorage:', error);
      return [];
    }
  }

  /**
   * Add a new notification
   */
  addNotification(notification) {
    try {
      const notifications = this.getNotifications();

      const newNotification = {
        id: Date.now().toString(),
        title: notification.title,
        body: notification.body,
        notification_type: notification.notification_type || 'default',
        data: notification.data || {},
        timestamp: new Date().toISOString(),
        read: false,
      };

      // Add to beginning of array
      notifications.unshift(newNotification);

      // Keep only the last MAX_NOTIFICATIONS
      const trimmed = notifications.slice(0, MAX_NOTIFICATIONS);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));

      // Dispatch event so UI can update
      window.dispatchEvent(new CustomEvent('notifications:updated', {
        detail: { notifications: trimmed }
      }));

      console.log('✅ Notification stored locally:', newNotification);
      return newNotification;
    } catch (error) {
      console.error('Error storing notification:', error);
      return null;
    }
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId) {
    try {
      const notifications = this.getNotifications();
      const updated = notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      );

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      // Dispatch event
      window.dispatchEvent(new CustomEvent('notifications:updated', {
        detail: { notifications: updated }
      }));

      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead() {
    try {
      const notifications = this.getNotifications();
      const updated = notifications.map(n => ({ ...n, read: true }));

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      // Dispatch event
      window.dispatchEvent(new CustomEvent('notifications:updated', {
        detail: { notifications: updated }
      }));

      return true;
    } catch (error) {
      console.error('Error marking all as read:', error);
      return false;
    }
  }

  /**
   * Delete a notification
   */
  deleteNotification(notificationId) {
    try {
      const notifications = this.getNotifications();
      const filtered = notifications.filter(n => n.id !== notificationId);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));

      // Dispatch event
      window.dispatchEvent(new CustomEvent('notifications:updated', {
        detail: { notifications: filtered }
      }));

      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  }

  /**
   * Clear all notifications (called on logout)
   */
  clearAll() {
    try {
      localStorage.removeItem(STORAGE_KEY);

      // Dispatch event
      window.dispatchEvent(new CustomEvent('notifications:updated', {
        detail: { notifications: [] }
      }));

      console.log('✅ All notifications cleared');
      return true;
    } catch (error) {
      console.error('Error clearing notifications:', error);
      return false;
    }
  }

  /**
   * Get unread notification count
   */
  getUnreadCount() {
    const notifications = this.getNotifications();
    return notifications.filter(n => !n.read).length;
  }

  /**
   * Get time ago string
   */
  getTimeAgo(timestamp) {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
}

// Export singleton instance
export const notificationStorageService = new NotificationStorageService();
export default notificationStorageService;
